"use client";

import { ChevronDown } from "lucide-react";
import { useState } from "react";

import { cn } from "../utils";

export type ProgressFieldInfo = {
  name: string;
  label: string;
  value?: string;
  status: "collected" | "remaining" | "optional";
};

export type ProgressBarProps = {
  completed: number;
  total: number;
  sectionLabel: string;
  fields?: ProgressFieldInfo[];
  className?: string;
};

export function ProgressBar({
  completed,
  total,
  sectionLabel,
  fields,
  className,
}: ProgressBarProps) {
  const [expanded, setExpanded] = useState(false);

  const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
  const hasFields = fields && fields.length > 0;

  const collectedFields = fields?.filter((f) => f.status === "collected") ?? [];
  const remainingFields = fields?.filter((f) => f.status === "remaining") ?? [];
  const optionalFields = fields?.filter((f) => f.status === "optional") ?? [];

  return (
    <div className={cn("bg-widget-bg rounded-2xl p-4 shadow-sm", className)}>
      <div
        className={cn("flex items-center justify-between mb-2", hasFields && "cursor-pointer")}
        onClick={() => hasFields && setExpanded(!expanded)}
      >
        <span className="text-xs font-medium text-text-secondary uppercase tracking-wide">
          {sectionLabel}
        </span>
        <div className="flex items-center gap-2">
          <span className="text-xs text-text-secondary">
            {completed}/{total} complete
          </span>
          {hasFields && (
            <ChevronDown
              className={cn(
                "size-4 text-text-secondary transition-transform duration-200",
                expanded && "rotate-180",
              )}
            />
          )}
        </div>
      </div>

      <div className="w-full h-2 bg-border rounded-full overflow-hidden">
        <div
          className="h-full bg-accent rounded-full transition-all duration-500"
          style={{ width: `${percent}%` }}
        />
      </div>

      {expanded && hasFields && (
        <div className="mt-3 space-y-3">
          {collectedFields.length > 0 && (
            <FieldGroup label="Collected">
              {collectedFields.map((f) => (
                <div key={f.name} className="flex items-center justify-between py-1.5 px-2">
                  <div className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 shrink-0" />
                    <span className="text-xs text-text-primary">{f.label}</span>
                  </div>
                  <span className="text-xs text-text-secondary">{f.value}</span>
                </div>
              ))}
            </FieldGroup>
          )}

          {remainingFields.length > 0 && (
            <FieldGroup label="Remaining">
              {remainingFields.map((f) => (
                <div key={f.name} className="flex items-center gap-2 py-1.5 px-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-border shrink-0" />
                  <span className="text-xs text-text-secondary">{f.label}</span>
                </div>
              ))}
            </FieldGroup>
          )}

          {optionalFields.length > 0 && (
            <FieldGroup label="Optional">
              {optionalFields.map((f) => (
                <div key={f.name} className="flex items-center gap-2 py-1.5 px-2">
                  <span className="w-1.5 h-1.5 rounded-full border border-border shrink-0" />
                  <span className="text-xs text-text-secondary">
                    {f.label}
                    {f.value && <span className="ml-1 text-text-primary">— {f.value}</span>}
                  </span>
                </div>
              ))}
            </FieldGroup>
          )}
        </div>
      )}
    </div>
  );
}

function FieldGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-[11px] font-medium text-text-secondary uppercase tracking-wider mb-1.5">
        {label}
      </p>
      <div className="space-y-1">{children}</div>
    </div>
  );
}
